/**
 * How big each open map is, against the limit: 10 MB open, what a device must
 * hold and draw (Phase 5, decided 2026-10-08; docs/internal/decisions.md).
 * The size is the map's content unpacked (`Y.encodeStateAsUpdate`), the same
 * on every device, so everyone on a map sees the same state.
 *
 * - From 80% the size shows "85% full" (interfaces/classic/ui/storage-usage.js), and a notice
 *   says so once (interfaces/classic/ui/map-size-notice.js).
 * - At the limit the map is full: read-only like a view-only map
 *   (engine/sync/share-state.js, CRDTBridge.isNodeReadOnly), except deleting and
 *   cutting, so the user can make room. Below the limit it is editable again.
 *   Sync is never blocked: other people's changes still arrive, and the app
 *   unpacks up to 16 MB (engine/sync/compression.js), well above the limit.
 *
 * Measuring encodes the whole map (about 50 ms at 8 MB on a laptop), so it
 * runs only near the limit: each change adds its length to an upper bound of
 * the size (a change never grows the map by more than its own length), and
 * the map is measured once that bound reaches 80%, when the changes pause.
 * The bound may stay above the size after deletions: then the map is only
 * measured more often.
 */
import * as Y from "yjs";
import { Events, emit } from "../core/events.js";

export const MAP_SIZE_LIMIT = 10 * 1024 * 1024;
const WARN_RATIO = 0.8;
// Debounce: measure once the changes pause, not at every keystroke.
const SETTLE_MS = 1000;

let limit = MAP_SIZE_LIMIT;
/** docId → { doc, bound, bytes, level, percent, timer } */
const maps = new Map();

function levelOf(bytes) {
  if (bytes >= limit) return "full";
  if (bytes >= limit * WARN_RATIO) return "warning";
  return "normal";
}

function measure(docId, entry) {
  clearTimeout(entry.timer);
  entry.timer = null;
  if (maps.get(docId) !== entry) return null; // replaced by a newer watch
  const update = Y.encodeStateAsUpdate(entry.doc);
  let bytes = update.length;
  // Undo keeps what this session deleted (Ctrl+Z restores it), so this
  // device's copy still holds it; a device opening the map does not. At the
  // limit, measure what a device opening the map holds: deleting makes room
  // at once. Loading a copy costs more (about 0.3 s at 8 MB on a laptop),
  // hence only here.
  if (bytes >= limit) bytes = loadedSize(update);
  entry.bytes = entry.bound = bytes;
  const level = levelOf(bytes);
  const percent = Math.floor((bytes / limit) * 100);
  if (level === entry.level && percent === entry.percent) return bytes;
  entry.level = level;
  entry.percent = percent;
  emit(Events.MAP_SIZE_CHANGED, { docId, level, percent });
  return bytes;
}

function loadedSize(update) {
  const copy = new Y.Doc();
  try {
    Y.applyUpdate(copy, update);
    return Y.encodeStateAsUpdate(copy).length;
  } finally {
    copy.destroy();
  }
}

/**
 * Watch an open map's size (CRDTBridge: the open map and its linked maps).
 * @param {string} docId
 * @param {Y.Doc} doc
 * @returns {() => void} stops watching
 */
export function trackMapSize(docId, doc) {
  const previous = maps.get(docId);
  if (previous) clearTimeout(previous.timer);
  const entry = { doc, bound: 0, bytes: null, level: "normal", percent: 0, timer: null };
  maps.set(docId, entry);
  const onUpdate = (update) => {
    entry.bound += update.length;
    if (entry.bound < limit * WARN_RATIO) return;
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => measure(docId, entry), SETTLE_MS);
  };
  doc.on("update", onUpdate);
  // Content loaded before watching is not in the bound.
  if (doc.store.clients.size) measure(docId, entry);
  return () => {
    doc.off("update", onUpdate);
    clearTimeout(entry.timer);
    // A newer watch on the same map (reopened) stays.
    if (maps.get(docId) !== entry) return;
    maps.delete(docId);
    if (entry.level !== "normal") emit(Events.MAP_SIZE_CHANGED, { docId, level: "normal", percent: 0 });
  };
}

/** Measure a watched map now (checks; a size shown right after a change). */
export function measureMapSize(docId) {
  const entry = maps.get(docId);
  return entry ? measure(docId, entry) : null;
}

/**
 * The map's size against the limit, once it has been measured (from 80%).
 * @returns {{ bytes: number, percent: number, level: 'normal'|'warning'|'full' }|null}
 */
export function mapSizeInfo(docId) {
  const entry = maps.get(docId);
  if (!entry || entry.bytes === null) return null;
  return { bytes: entry.bytes, percent: entry.percent, level: entry.level };
}

/** What a device opening the watched map holds, in bytes (checks). */
export function loadedMapSize(docId) {
  const entry = maps.get(docId);
  return entry ? loadedSize(Y.encodeStateAsUpdate(entry.doc)) : null;
}

/** True when the map is at or above the limit: read-only except deleting. */
export function isMapFull(docId) {
  return !!docId && maps.get(docId)?.level === "full";
}

/** Change the limit (checks use a small one), or restore it with no value. */
export function setMapSizeLimit(bytes = MAP_SIZE_LIMIT) {
  limit = bytes;
  for (const [docId, entry] of maps) measure(docId, entry);
}
